/**
 * Typed `localStorage` wrapper for the dashboard session. The dashboard is
 * account-scoped and stateless beyond the bearer session, so this holds only
 * the session blob (no wallet cache like the wallet app).
 *
 * The persisted session is NOT trusted for authorization — every server call
 * re-validates the bearer token against the active session.
 */

const SESSION_KEY = "ggcoin.dashboard.session";

/** Subset of `LoginResponse` held between page loads. */
export interface PersistedSession {
  jwt: string;
  accountId: string;
  sessionId: string;
  expiresAt: string;
}

/** Storage abstraction so tests can swap in a Map-backed fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** In-memory fallback for tests + the (very short) SSR window. */
export class MemoryStorage implements StorageLike {
  private readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

/**
 * Resolve a usable `localStorage`, else memory. Safari private mode and
 * quota-exhausted profiles expose `localStorage` but throw on access, so we
 * probe with a write/remove before trusting it.
 */
export function defaultStorage(): StorageLike {
  if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
    try {
      const storage = globalThis.localStorage;
      const probe = "__ggcoin.dashboard.probe__";
      storage.setItem(probe, "1");
      storage.removeItem(probe);
      return storage;
    } catch {
      // Restricted/unavailable — fall through to the in-memory store.
    }
  }
  return new MemoryStorage();
}

export class SessionStore {
  constructor(private readonly storage: StorageLike = defaultStorage()) {}

  read(): PersistedSession | null {
    // getItem itself can throw in restricted modes — treat any failure as
    // "no session" rather than crashing the auth path.
    let raw: string | null;
    try {
      raw = this.storage.getItem(SESSION_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as PersistedSession;
      if (!parsed?.jwt || !parsed.accountId || !parsed.sessionId) return null;
      // Expiry is server-stamped ISO 8601; a past or unparseable timestamp
      // reads as no session so a stale blob never authenticates.
      const expiresAtMs = new Date(parsed.expiresAt).getTime();
      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  write(session: PersistedSession): void {
    try {
      this.storage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      // Non-fatal: the in-memory client token still keeps this session live.
    }
  }

  clear(): void {
    try {
      this.storage.removeItem(SESSION_KEY);
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }
}
